import React, { useState } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  achievement: {
    title: string;
    description: string;
    image?: string;
    type: 'badge' | 'level' | 'mission' | 'reward' | 'victory';
  };
}

export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, achievement }) => {
  const [customMessage, setCustomMessage] = useState('');
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = window.location.origin + '/premiado/home';
  const defaultMessage = `🎉 Acabei de conquistar: ${achievement.title}! ${achievement.description}`;
  const fullMessage = customMessage || defaultMessage;

  const hashtags = ['Conquista', 'Gamificacao', 'Sucesso', 'Motivacao'];

  const socialNetworks = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'bg-blue-600 hover:bg-blue-700',
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(fullMessage)}`,
          '_blank',
          'width=600,height=400'
        );
      }
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-fill',
      color: 'bg-black hover:bg-gray-800',
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(fullMessage)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags.join(',')}`,
          '_blank',
          'width=600,height=400'
        );
      }
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'bg-blue-700 hover:bg-blue-800',
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      }
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'bg-green-600 hover:bg-green-700',
      action: () => {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(fullMessage + ' ' + shareUrl)}`,
          '_blank'
        );
      }
    },
    {
      name: 'Telegram',
      icon: 'ri-telegram-fill',
      color: 'bg-sky-500 hover:bg-sky-600',
      action: () => {
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(fullMessage)}`,
          '_blank'
        );
      }
    }
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fullMessage + '\n' + shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-share-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Compartilhar Conquista</h2>
                <p className="text-white/80 text-sm">Mostre seu progresso para o mundo!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Preview da Conquista */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-200">
            <div className="flex items-start gap-4">
              {achievement.image && (
                <img
                  src={achievement.image}
                  alt={achievement.title}
                  className="w-20 h-20 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{achievement.title}</h3>
                <p className="text-gray-600">{achievement.description}</p>
              </div>
            </div>
          </div>

          {/* Mensagem Personalizada */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Personalize sua mensagem (opcional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={defaultMessage}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all resize-none"
              rows={3}
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                {customMessage.length}/280 caracteres
              </p>
              <button
                onClick={() => setCustomMessage('')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                Usar mensagem padrão
              </button>
            </div>
          </div>

          {/* Hashtags Sugeridas */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Hashtags sugeridas
            </label>
            <div className="flex flex-wrap gap-2">
              {hashtags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Redes Sociais */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Compartilhar em
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {socialNetworks.map((network) => (
                <button
                  key={network.name}
                  onClick={network.action}
                  className={`${network.color} text-white rounded-lg p-4 flex flex-col items-center gap-2 transition-all hover:scale-105 active:scale-95`}
                >
                  <i className={`${network.icon} text-3xl`}></i>
                  <span className="text-sm font-medium">{network.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Copiar Link */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ou copie o link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm text-gray-600"
              />
              <button
                onClick={copyToClipboard}
                className={`px-6 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-teal-500 text-white hover:bg-teal-600'
                }`}
              >
                {copied ? (
                  <>
                    <i className="ri-check-line mr-2"></i>
                    Copiado!
                  </>
                ) : (
                  <>
                    <i className="ri-file-copy-line mr-2"></i>
                    Copiar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Instagram */}
          <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <i className="ri-instagram-fill text-3xl"></i>
                <div>
                  <p className="font-semibold">Instagram</p>
                  <p className="text-sm text-white/80">Copie a mensagem e cole no seu story</p>
                </div>
              </div>
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                Copiar texto
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
